import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash-es/debounce';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';
import { useClickAway } from 'react-use';

import { BlockNoteEditor } from '@/components/atoms/BlockNoteEditor/BlockNoteEditor';
import { cn } from '@/lib/utils';
import { Option } from '@/shared/types/rules';

interface Props<T extends FieldValues = FieldValues> {
  // Form integration
  name: string;
  setValue?: UseFormSetValue<T>;
  defaultValue?: string;
  value?: string;

  // UI features
  className?: string;
  error?: boolean;
  errorMessage?: React.ReactNode | string;
  placeholder?: string;

  // Behavior
  shouldExpand?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  reset?: boolean;
  onClick?: () => void;

  // Event handlers (for non-RHF consumers)
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;

  // Variants
  maxCharacters?: number;

  // Variables
  variableOptions?: Option[];
}

const NO_VARIABLE_OPTIONS: Option[] = [];

/**
 * BlockNote-based markdown editor with React Hook Form integration
 * Replaces the legacy Plate-based MarkdownEditor with modern BlockNote editor
 */
export default function MarkdownEditorBlockNote<T extends FieldValues = FieldValues>({
  name,
  setValue,
  defaultValue = '',
  value,
  className,
  error,
  errorMessage,
  placeholder,
  shouldExpand = false,
  disabled = false,
  readOnly = false,
  reset = false,
  onClick,
  onChange,
  onFocus,
  onBlur,
  maxCharacters,
  variableOptions = NO_VARIABLE_OPTIONS,
}: Props<T>) {
  const [expanded, setExpanded] = useState(false);
  const [content, setContent] = useState<string>(value || defaultValue || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstChangeRef = useRef(true);
  const isResettingRef = useRef(false);
  // Handle reset: reapply defaultValue (or empty string) and propagate through setValue
  useEffect(() => {
    if (reset) {
      isResettingRef.current = true;
      const resetValue = defaultValue || '';
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setContent(resetValue);

      // Propagate reset through setValue to keep form state consistent
      if (setValue) {
        setValue(name as Path<T>, resetValue as PathValue<T, Path<T>>, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }

      // Call onChange for non-RHF consumers
      onChange?.(resetValue);

      // Reset first-change flag to avoid validation on next edit
      isFirstChangeRef.current = true;

      // Clear resetting flag after a tick to allow value-sync to resume
      setTimeout(() => {
        isResettingRef.current = false;
      }, 0);
    }
  }, [reset, defaultValue, setValue, name, onChange]);

  // Sync with controlled value (guard while resetting to avoid overwrite)
  useEffect(() => {
    if (!isResettingRef.current && value !== undefined && value !== content) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setContent(value);
    }
  }, [value, content]);

  // Handle content changes with debouncing
  const handleContentChange = useCallback(
    (newContent: string) => {
      setContent(newContent);

      if (setValue) {
        // Skip validation on first change to avoid premature error display
        const shouldValidate = !isFirstChangeRef.current && !!error;
        setValue(name as Path<T>, newContent as PathValue<T, Path<T>>, {
          shouldDirty: true,
          shouldValidate,
        });

        if (isFirstChangeRef.current) {
          isFirstChangeRef.current = false;
        }
      }

      // Call onChange for non-RHF consumers
      onChange?.(newContent);
    },
    [setValue, name, error, onChange]
  );

  // Debounced version for BlockNoteEditor
  const debouncedHandleContentChange = useMemo(
    () => debounce(handleContentChange, 100),
    [handleContentChange]
  );

  // Cleanup debounced handler on unmount to prevent stale updates
  useEffect(() => {
    return () => {
      debouncedHandleContentChange.cancel();
    };
  }, [debouncedHandleContentChange]);

  // Handle container click for expansion and focus
  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || readOnly) {
        return;
      }

      const editable = containerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]');

      // Skip when click is inside editor — focusing would close BlockNote overlays (e.g. table handles).
      if (editable && !editable.contains(e.target as Node)) {
        editable.focus();
      }

      if (shouldExpand && !expanded) {
        setExpanded(true);
        setTimeout(() => {
          onClick?.();
        }, 0);
      }
    },
    [disabled, readOnly, shouldExpand, expanded, onClick]
  );

  // Handle focus
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Handle blur
  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  // Click away handler for collapse
  useClickAway(containerRef, () => {
    if (shouldExpand && expanded) {
      // Delay to allow click events to propagate
      setTimeout(() => {
        setExpanded(false);
      }, 500);
    }
  });

  // Container classes with Tailwind
  const containerClasses = cn(
    // Base styles
    'relative w-full cursor-text rounded-md border transition-all duration-200',
    'bg-background',
    'min-h-[100px]',

    // The invalid border and ring come from `aria-invalid`, owned by the editor's stylesheet.
    !error && 'border-stroke focus-within:border-primary hover:border-foreground-muted',

    // Disabled/readonly states
    (disabled || readOnly) && 'cursor-not-allowed bg-muted opacity-50',

    // Expansion
    expanded && 'min-h-[300px]',

    className
  );

  // Character counter display
  const characterCount = content?.length || 0;
  const showCharacterCounter = maxCharacters !== undefined;

  return (
    <div className="w-full">
      {/* Editor container */}
      <div
        ref={containerRef}
        onClick={handleContainerClick}
        onKeyDown={() => {}}
        role="textbox"
        tabIndex={-1}
      >
        <BlockNoteEditor
          content={content}
          onContentChange={debouncedHandleContentChange}
          formattingButtons={['bold', 'italic', 'code', 'link']}
          allowedSlashKeys={['numbered_list', 'bullet_list']}
          variableOptions={variableOptions}
          placeholder={placeholder}
          editable={!disabled && !readOnly}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={maxCharacters}
          enforceMaxLength={!!maxCharacters}
          className={containerClasses}
          aria-invalid={error}
        />
      </div>

      {/* Error message */}
      {errorMessage && <p className="mt-1 text-sm text-warning">{errorMessage}</p>}

      {/* Character counter */}
      {showCharacterCounter && (
        <p className="mt-1 text-right text-xs text-foreground-muted">
          {characterCount}/{maxCharacters}
        </p>
      )}
    </div>
  );
}
